import {CloseOutlined} from '@mui/icons-material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DoneOutlined from '@mui/icons-material/DoneOutlined';
import {
  Alert,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useColorScheme,
} from '@mui/material';
import {useQueryClient} from '@tanstack/react-query';
import PropTypes from 'prop-types';
import React, {memo, useState} from 'react';
import {useDispatch} from 'react-redux';
import ReviewUploadDialog from './ReviewUploadDialog.jsx';
import UploadsListActionsMenu from './UploadsListActionsMenu.jsx';
import {formatUploadName} from './util.js';
import {showLeftDrawer} from '@actions/ui';
import {queryKeys, useDeleteUpload} from '@api/asyncIngest';
import {notify} from '@app/actions/notifications';
import {trackEvent} from '@app/analytics.js';
import {DRAWERS} from '@app/constants';
import {UPLOAD_LABELS, UPLOAD_STATES} from '@app/constants/uploads.js';
import useCollectionParams from '@app/hooks/useCollectionParams';
import {formatMedDateTime} from '@app/util/format-dates';

const statusIcons = {
  [UPLOAD_STATES.CREATED]: <CircularProgress size={16} />,
  [UPLOAD_STATES.PENDING]: <CircularProgress size={16} />,
  [UPLOAD_STATES.PROCESSING]: <CircularProgress size={16} />,
  [UPLOAD_STATES.MIGRATING]: <CircularProgress size={16} />,
  [UPLOAD_STATES.DONE]: <DoneOutlined color="primary" />,
};

const UploadItem = ({upload}) => {
  const queryClient = useQueryClient();

  const [review, setReview] = useState(false);
  const {setCollection} = useCollectionParams();
  const dispatch = useDispatch();
  // count number of errors and alternates
  const numErrors = upload.total_errors;
  const numAlternates = upload.total_alternates;
  const numToReview = upload.total_to_review;

  const {colorScheme} = useColorScheme();
  const alertVariant = colorScheme === 'dark' ? 'outlined' : 'standard';

  const handleClose = () => {
    setReview(false);
    trackEvent('Async Upload', {
      action: 'closed review modal',
      uploadId: upload.id,
    });
    queryClient.invalidateQueries({queryKey: queryKeys.lists()});
  };

  const requiresReview =
    upload.state === UPLOAD_STATES.REVIEW &&
    (numErrors > 0 || numAlternates > 0);

  /* displays information if this upload requires attention from the user */
  const InfoBox = () => {
    const messageColor = 'warning';

    let message;

    if (!numErrors && !numToReview) {
      return;
    }

    // show an error alert if all features errored
    // otherwise use a warning.
    if (numErrors > 0) {
      /* create a message displaying both errors and num features to review. There's a test for each combination. */
      const alternateMessage = ` and ${numToReview} feature${numToReview > 1 ? 's' : ''} requiring simplification`;
      message = `${numErrors} error${numErrors > 1 ? 's' : ''}${
        numToReview > 0 ? alternateMessage : ''
      }`;
    } else if (numToReview > 0) {
      /* in most cases, we just need to show how many features still need to be accepted. */
      message = `Simplify ${numToReview} feature${numToReview > 1 ? 's' : ''}`;
    }

    return (
      <Alert
        severity={messageColor}
        sx={{
          paddingX: 1,
          paddingY: 0,
          marginTop: 1,
        }}
        variant={alertVariant}
      >
        {message}
      </Alert>
    );
  };

  const UploadErrorMsg = () => {
    const msg = upload?.error;
    return (
      <Alert
        severity="error"
        sx={{marginTop: 1, paddingY: 0, paddingX: 1}}
        variant={alertVariant}
      >
        {msg}
      </Alert>
    );
  };

  const viewCollection = () => {
    setCollection(upload?.collection, true);
    dispatch(showLeftDrawer(DRAWERS.COLLECTIONS));
    trackEvent('Async Upload', {
      action: 'clicked view imported collection',
      uploadId: upload.id,
    });
  };

  /* displays the most relevant timestamp (either completed at or uploaded at) */
  const Timestamp = () => {
    let timestamp = upload.created_at;
    if (upload.completed_at) {
      timestamp = upload.completed_at;
    }
    const formattedDate = formatMedDateTime(
      timestamp,
      `Timestamp missing for upload ID ${upload.id}`
    );

    return (
      <Typography variant="body2" sx={{color: 'text.secondary'}}>
        Uploaded: <time dateTime={formattedDate}>{formattedDate}</time>
      </Typography>
    );
  };

  const getUploadButtons = uploadState => {
    // eslint-disable-next-line default-case
    switch (uploadState) {
      case UPLOAD_STATES.REVIEW:
      case UPLOAD_STATES.INGESTED:
        return (
          <Button
            onClick={() => setReview(true)}
            color="primary"
            variant="contained"
            sx={{
              height: theme => theme.spacing(3.75),
              minWidth: theme => theme.spacing(10),
              flexShrink: 0,
            }}
          >
            Complete import
          </Button>
        );
      case UPLOAD_STATES.DONE:
        return (
          <Button
            aria-label="View collection"
            onClick={viewCollection}
            color="primary"
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            sx={{
              height: theme => theme.spacing(3.75),
              minWidth: theme => theme.spacing(10),
              flexShrink: 0,
            }}
          >
            View
          </Button>
        );
    }
  };

  const {mutate: deleteUpload} = useDeleteUpload(
    {upload},
    {
      onSettled: () => {
        trackEvent('Async Upload', {
          action: 'clicked delete upload',
          uploadId: upload.id,
        });
      },
      onError: () => {
        dispatch(
          notify({
            message:
              'There was an error deleting the upload. Please try again.',
            severity: 'error',
          })
        );
      },
    }
  );

  return (
    <>
      <Paper
        variant="outlined"
        elevation={0}
        sx={{
          padding: 1,
        }}
      >
        <Stack
          direction="row"
          sx={{alignItems: 'center', justifyContent: 'space-between'}}
        >
          <Typography
            variant="subtitle1"
            noWrap
            sx={{
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            }}
          >
            {formatUploadName(upload)}
          </Typography>

          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              gap: 1,
            }}
          >
            {![
              UPLOAD_STATES.REVIEW,
              UPLOAD_STATES.INGESTED,
              UPLOAD_STATES.ERROR,
            ].includes(upload.state) && (
              <>
                <Tooltip title={UPLOAD_LABELS[upload.state]}>
                  {statusIcons[upload.state]}
                </Tooltip>
                <Divider orientation="vertical" variant="middle" flexItem />
              </>
            )}
            {![
              UPLOAD_STATES.CREATED,
              UPLOAD_STATES.PENDING,
              UPLOAD_STATES.PROCESSING,
              UPLOAD_STATES.MIGRATING,
            ].includes(upload.state) && (
              <UploadsListActionsMenu
                actions={[
                  {
                    label: 'Clear',
                    onClick: deleteUpload,
                    icon: <CloseOutlined />,
                  },
                ]}
              />
            )}
          </Stack>
        </Stack>
        <Timestamp />
        {requiresReview && <InfoBox />}
        {upload?.error ? (
          <UploadErrorMsg />
        ) : (
          <Stack
            direction="row"
            sx={{
              gap: 1,
              marginTop: 2,
              justifyContent: 'flex-end',
              alignItems: 'center',
              display: 'flex',
            }}
          >
            {getUploadButtons(upload.state)}
          </Stack>
        )}
      </Paper>
      {review && (
        <ReviewUploadDialog
          upload={upload}
          open={review}
          onClose={handleClose}
        />
      )}
    </>
  );
};

UploadItem.propTypes = {
  upload: PropTypes.object,
};

export default memo(UploadItem);
